import {
  UserRoleType,
  ReactionType,
  SearchFilter,
  SearchFilterType,
  MessageThreadType,
} from "gql/graphql";
import moment, { CalendarSpec, Moment, MomentInput } from "moment";
import { ChangeEvent } from "react";
import colors from "app/colors";
import {
  FlexibleOption,
  FlexibleOptionList,
  FullCombinator,
} from "react-querybuilder";

export const ACCESS_TOKEN = "accessToken";

export const DEFAULT_PAGE_SIZE = 20;
export const INTERNAL_ADMIN_PAGE_SIZE = 20;
export const MAX_PINNED_MESSAGE_THREADS = 12;
export const MESSAGE_THREAD_PREVIEW_HEIGHT = 92;
export const FILE_COMPRESSION_BYTE_COUNT_THRESHOLD = 200000;
export const FILE_THUMB_CONTENT_TYPE = "image/jpeg";
export const FILE_GALLERY_BUTTON_BACKGROUND_COLOR = colors.gray90;
export const FILE_GALLERY_BUTTON_BOX_SIZE = 6;
export const FILE_GALLERY_BUTTON_WIDTH = 20;
export const MESSAGE_LIST_PADDING = 4;

export const MONTH_DAY_YEAR_FORMAT = "MMM DD YYYY";
export const FILE_DAY_YEAR_FORMAT = "MMM DD YYYY [at] h:mm A";
export const OOO_TIME_FORMAT = "MMMM D";

export const MESSAGE_NOT_SENT_OPACITY = 0.4;
export const MESSAGE_NOT_SENT_SPINNER_SPEED = "1.0s";

export const HASH_KEY = "#";
export const CALLOUT_KEY = "@";

export const TAG_MARKDOWN = "tid";
export const TAG_REGEX = /\{\{tid=(?<id>[0-9a-fA-F]{24})\}\}/g;
export const NEW_TAG_NAME_REGEX = /^[a-z0-9_\-.]+$/;
export const NEW_TAG_NAME_REGEX_MULTIPLE_TAGS = /^[a-z0-9_\-.,]+$/;
export const MENTION_ALLOWED_CHARS_REGEX = /^[A-Za-z0-9\-_.]*$/;

export const USER_MARKDOWN = "uid";
export const USER_REGEX = /\{\{uid=(?<id>[0-9a-fA-F]{24})\}\}/g;

export const DISTRIBUTION_GROUP_MARKDOWN = "did";
export const DISTRIBUTION_GROUP_REGEX = /\{\{did=(?<id>[0-9a-fA-F]{24})\}\}/g;
export const NEW_DISTRIBUTION_GROUP_NAME_REGEX = /^[a-z0-9_\-.]+$/;

export const DATE_MARKDOWN = "date";

export const OFFICE_BASE_URL =
  "https://view.officeapps.live.com/op/embed.aspx?src=";

export const TYPING_TIMEOUT_MILLISECONDS = 5000;
export const TYPING_SEND_INTERVAL_MILLISECONDS = 3000; // this should always be less than TYPING_TIMEOUT_MILLISECONDS to prevent flickers in the UI when the user is taking forever typing a long message

export const SERVER_ERROR = "SERVER_ERROR";

export const NO_SUBJECT = "(no subject)";

export type MESSAGE_STATE = "not-sent" | "sent" | "read-only";

export type HIGHLIGHT_PATHS =
  | "subject"
  | "search"
  | "localizedText"
  | "html"
  | "fileList.name"
  | "fileList.voiceNoteSearch"
  | "quotedMessageList.search";

export type FORWARD_TO_STATE =
  | "select-messages"
  | "select-forward-to"
  | "reply"
  | "compose";

export const REACTION_TYPES = [
  ReactionType.Like,
  ReactionType.Heart,
  ReactionType.Dislike,
  ReactionType.Laughing,
  ReactionType.Surprise,
];

function addYearToFormat_for_MESSAGE_THREAD_DATE_SEPERATOR_FORMAT(
  this: Moment,
  now?: MomentInput,
) {
  const nowYear = moment(now).year();
  const thisYear = this.year();

  return `ddd MMM DD ${nowYear !== thisYear ? "YYYY" : ""}`;
}

export const MESSAGE_THREAD_DATE_SEPERATOR_FORMAT = {
  sameDay: "[Today]",
  lastDay: "[Yesterday]",
  lastWeek: addYearToFormat_for_MESSAGE_THREAD_DATE_SEPERATOR_FORMAT,
  sameElse: addYearToFormat_for_MESSAGE_THREAD_DATE_SEPERATOR_FORMAT,
  lastElse: addYearToFormat_for_MESSAGE_THREAD_DATE_SEPERATOR_FORMAT,
};

function addYearToFormat_for_INBOX_MESSAGE_THREAD_CALENDAR_FORMATS(
  this: Moment,
  now?: MomentInput,
) {
  const nowYear = moment(now).year();
  const thisYear = this.year();

  return `ddd M/D${nowYear !== thisYear ? "/YY" : ""}`;
}

export const INBOX_MESSAGE_THREAD_CALENDAR_FORMATS = {
  sameDay: "h:mm A",
  lastDay: "ddd h:mm A",
  lastWeek: "ddd h:mm A",
  lastElse: addYearToFormat_for_INBOX_MESSAGE_THREAD_CALENDAR_FORMATS,
  sameElse: addYearToFormat_for_INBOX_MESSAGE_THREAD_CALENDAR_FORMATS,
};

function addYearToFormat_for_MESSAGE_CALENDAR_FORMATS(
  this: Moment,
  now?: MomentInput,
) {
  const nowYear = moment(now).year();
  const thisYear = this.year();

  return `ddd M/D${nowYear !== thisYear ? "/YY" : ""} h:mm A`;
}

export const MESSAGE_CALENDAR_FORMATS = {
  sameDay: "h:mm A",
  lastDay: "[Yesterday] h:mm A",
  lastWeek: "ddd h:mm A",
  lastElse: addYearToFormat_for_MESSAGE_CALENDAR_FORMATS,
  sameElse: addYearToFormat_for_MESSAGE_CALENDAR_FORMATS,
  sameYear: "MMM DD",
};

export const COMMON_DATE_CALENDAR_FORMATS: CalendarSpec = {
  sameDay: "[Today]",
  lastDay: "[Yesterday]",
  nextDay: "[Tomorrow]",
  nextWeek: "MMM DD",
  lastWeek: "MMM DD",
  sameYear: "MMM DD",
  sameElse(m: MomentInput, now?: Moment): string {
    const _m = this as unknown as Moment;
    const ref = now ?? moment();
    return _m.year() < ref.year() ? "MMM DD YYYY" : "MMM DD";
  },

  lastElse: "MMM DD YYYY",
};

export enum MENTION_TYPE {
  TAG = "TAG",
  USER = "USER",
  DISTRIBUTION_GROUP = "DISTRIBUTION_GROUP",
}

export const INTERACTIVE_USER_ROLE_TYPE_LIST: Array<UserRoleType> = [
  UserRoleType.User,
  UserRoleType.Admin,
];

export const AUTH_REDIRECT_PATH = "/auth-redirect";
export const AUTH_PATH = "/auth";
export const LOGOUT_PATH = "/logout";
export const HOME_PATH = "/home";
export const THREADS_PATH = "/threads";
export const TASKS_PATH = "/tasks";
export const STARRED_THREADS_PATH = "/starred-threads";
export const SENT_THREADS_PATH = "/sent-threads";
export const REMINDERS_THREADS_PATH = "/reminders";
export const ALL_THREADS_PATH = "/all-threads";
export const TAG_GROUP_THREADS_PATH = "/tag-group-threads";
export const CHATS_PATH = "/chats";
export const ALL_CHATS_PATH = "/all-chats";
export const CONTACTS_PATH = "/contacts";
export const ADMIN_PATH = "/admin";
export const INTERNAL_ADMIN_PATH = "/internal-admin";
export const SEARCH_PATH = "/search";
export const INVITE_PATH = "/invite";
export const UNSUBSCRIBE_PATH = "/unsubscribe";

export const MOBILE_PATH = "/mobile";

export const SKIP_AUTH_PATHS = [
  INVITE_PATH,
  MOBILE_PATH,
  AUTH_PATH,
  LOGOUT_PATH,
  AUTH_REDIRECT_PATH,
  "/",
];

export type CALLOUT_TYPE = "user" | "tag" | "distribution-group";

export const UNSEEN_CHATS_COUNT_SEARCH_FILTERS: SearchFilter[] = [
  { type: SearchFilterType.IsNotSeenByMe },
  { type: SearchFilterType.IsInbox },
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Chat],
  },
];

export const UNSEEN_MESSAGE_THREAD_COUNT_SEARCH_FILTERS: SearchFilter[] = [
  { type: SearchFilterType.IsNotSeenByMe },
  { type: SearchFilterType.IsInbox },
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Thread],
  },
];

export const UNSEEN_TASKS_SEARCH_FILTERS: SearchFilter[] = [
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Task],
  },
  { type: SearchFilterType.IsNotSeenByMe },
];

export const DEFAULT_HOME_SEARCH_FILTERS: SearchFilter[] = [
  { type: SearchFilterType.IsInbox },
  { type: SearchFilterType.IsNotPinnedByMe },
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Thread, MessageThreadType.Chat],
  },
];

export const DEFAULT_ALL_PINNED_SEARCH_FILTERS: SearchFilter[] = [
  { type: SearchFilterType.IsPinnedByMe },
];

export const DEFAULT_PINNED_DIRECT_MESSAGE_THREAD_SEARCH_FILTERS: SearchFilter[] =
  [
    { type: SearchFilterType.IsPinnedByMe },
    {
      type: SearchFilterType.TypeList,
      stringListValue: [MessageThreadType.Chat],
    },
  ];

export const DEFAULT_PINNED_MESSAGE_THREAD_SEARCH_FILTERS: SearchFilter[] = [
  { type: SearchFilterType.IsPinnedByMe },
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Thread],
  },
];

export const DEFAULT_PINNED_TASKS_SEARCH_FILTERS: SearchFilter[] = [
  { type: SearchFilterType.IsPinnedByMe },
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Task],
  },
];

export const DEFAULT_RECENT_SEARCH_FILTERS: SearchFilter[] = [
  { type: SearchFilterType.IsNotPinnedByMe },
];

export const DEFAULT_CHAT_SEARCH_FILTERS: SearchFilter[] = [
  { type: SearchFilterType.IsInbox },
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Chat],
  },
  { type: SearchFilterType.IsNotPinnedByMe },
];

export const DEFAULT_ALL_CHAT_SEARCH_FILTERS: SearchFilter[] = [
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Chat],
  },
];

export const DEFAULT_THREAD_SEARCH_FILTERS: SearchFilter[] = [
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Thread],
  },
  { type: SearchFilterType.IsInbox },
  { type: SearchFilterType.IsNotPinnedByMe },
];

export const DEFAULT_TASKS_SEARCH_FILTERS: SearchFilter[] = [
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Task],
  },
  { type: SearchFilterType.IsNotPinnedByMe },
];

export const DEFAULT_THREAD_STARRED_SEARCH_FILTERS: SearchFilter[] = [
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Thread],
  },
  { type: SearchFilterType.IsStarredByMe },
];

export const DEFAULT_THREAD_SENT_SEARCH_FILTERS: SearchFilter[] = [
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Thread],
  },
  { type: SearchFilterType.IsSentByMe },
];

export const DEFAULT_THREAD_REMINDERS_SEARCH_FILTERS: SearchFilter[] = [
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Thread],
  },
  { type: SearchFilterType.IsReminder },
];

export const DEFAULT_THREAD_ALL_SEARCH_FILTERS: SearchFilter[] = [
  {
    type: SearchFilterType.TypeList,
    stringListValue: [MessageThreadType.Thread],
  },
];

export const THREAD_DRAFT_ID = "thread";
export const CHAT_DRAFT_ID = "chat";
export const TASK_DRAFT_ID = "task";
export const FORWARD_DRAFT_ID = "forward-message";

export const HAPPYCO_THREAD_DRAFT_ID = "happyco-thread";

export const MESSAGE_REPLY_MAX_HEIGHT = 200;
export const INBOX_MIN_WIDTH = 630;
export const DRAWER_WIDTH = 533;
//This is an approximation of the components on top/bottom of the message list for the purpose of claculating a max height for the message reply component
export const DRAWER_WIDTH_PX = `${DRAWER_WIDTH}px`;
export const RESIZE_HANDLE_WIDTH = 12;

export const MAX_IMAGE_SIZE = 2048;
export const IMAGE_COMPRESSION_QUALITY = 80;
export const COMPRESSED_IMAGE_FORMAT = "JPEG";
export const HEIC_CONVERSION_IMAGE_FORMAT = "image/jpeg";
export const IMAGE_GALLERY_ITEM_MAX_HEIGHT = "calc(100vh - 160px)";

export enum DATE_RANGE_TYPE {
  LAST_24_HOURS = "LAST_24_HOURS",
  LAST_7_DAYS = "LAST_7_DAYS",
  LAST_30_DAYS = "LAST_30_DAYS",
  LAST_3_MONTHS = "LAST_3_MONTHS",
  THIS_YEAR = "THIS_YEAR",
  CUSTOM = "CUSTOM",
  TODAY = "TODAY", //for tasks
}

export type MessageThreadFilesDrawerTabType =
  | "ALL FILES"
  | "MEDIA"
  | "DOCS"
  | "LINKS";

export type TaskDueDateOption = "Today" | "Tomorrow" | "1 Week" | "Custom";

export const MESSAGE_THREAD_FILES_DRAWER_TAB_TYPES: Array<MessageThreadFilesDrawerTabType> =
  ["ALL FILES", "MEDIA", "DOCS", "LINKS"];

export const titleForDateRangeType = (type: DATE_RANGE_TYPE) => {
  switch (type) {
    case DATE_RANGE_TYPE.LAST_24_HOURS:
      return "Today";
    case DATE_RANGE_TYPE.LAST_7_DAYS:
      return "This Week";
    case DATE_RANGE_TYPE.LAST_30_DAYS:
      return "Last 30 Days";
    case DATE_RANGE_TYPE.LAST_3_MONTHS:
      return "Last 3 Months";
    case DATE_RANGE_TYPE.THIS_YEAR:
      return "This Year";
    case DATE_RANGE_TYPE.CUSTOM:
      return "Custom";
    case DATE_RANGE_TYPE.TODAY:
      return "Today";
    default:
      throw new Error(`Unhandled case in titleForDateRangeType: ${type}`);
  }
};

const CAN_USE_DOM: boolean =
  typeof window !== "undefined" &&
  typeof window.document !== "undefined" &&
  typeof window.document.createElement !== "undefined";

export const IS_APPLE: boolean =
  CAN_USE_DOM && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

export enum MessageSubType {
  LOCKED = "LOCKED",
  UNLOCKED = "UNLOCKED",
  NON_MEMBER_EMAIL_USERS_ADDED = "NON_MEMBER_EMAIL_USERS_ADDED",
  OUT_OF_OFFICE = "OUT_OF_OFFICE",
  WELCOME = "WELCOME",
}

export interface ICheckboxProps {
  [x: string]: any;
  onChange: (
    eventOrValue: ChangeEvent<HTMLInputElement> | string | number,
  ) => void;
}

export interface IEmailAddress {
  userId?: string;
  email: string;
  isValid: boolean;
}

export type UserPreferenceName =
  | "ENTER_KEY_SETTING"
  | "LOCALE"
  | "SMS"
  | "WEB_IS_HOME_GROUP"
  | "WEB_IS_HOME_MESSAGE_THREADS_EXPANDED"
  | "WEB_IS_HOME_DIRECT_MESSAGE_THREADS_EXPANDED"
  | "WEB_IS_HOME_PINNED_MESSAGE_THREADS_EXPANDED"
  | "WEB_IS_HOME_TASKS_EXPANDED"
  | "WEB_IS_PINNED_MESSAGE_THREADS_EXPANDED"
  | "WEB_IS_PINNED_DIRECT_MESSAGE_THREADS_EXPANDED"
  | "WEB_IS_PINNED_TASKS_EXPANDED"
  | "WEB_IS_HOME_UNREAD_FILTER_TOGGLED"
  | "WEB_IS_REMINDERS_UNREAD_FILTER_TOGGLED"
  | "WEB_HOME_DATE_FILTER"
  | "WEB_REMINDERS_DATE_FILTER"
  | "WEB_IS_CHATS_UNREAD_FILTER_TOGGLED"
  | "WEB_CHATS_DATE_FILTER"
  | "WEB_IS_THREADS_UNREAD_FILTER_TOGGLED"
  | "WEB_THREADS_DATE_FILTER"
  | "WEB_IS_TASKS_UNREAD_FILTER_TOGGLED"
  | "WEB_THREAD_CREATED_USER_FILTER"
  | "WEB_TASK_ASSIGNED_USER_FILTER"
  | "WEB_TASK_CREATED_USER_FILTER"
  | "WEB_TASK_STATUS_FILTER"
  | "WEB_SORT_TYPE"
  | "DISPLAY_INVITE_BANNER";

export type TextAlignment = "inline" | "bottom";

export const REACTION_ORDER = [
  ReactionType.Like,
  ReactionType.Heart,
  ReactionType.Dislike,
  ReactionType.Laughing,
  ReactionType.Surprise,
];

export const QUERY_OPERATORS: FlexibleOptionList<FlexibleOption> = [
  { name: "in", label: "is one of" },
  { name: "notIn", label: "is not any of" },
];

export const QUERY_COMBINATORS: FlexibleOptionList<FullCombinator> = [
  { name: "and", label: "ALL RULES" },
  { name: "or", label: "ANY RULES" },
];
